import type { AppDispatch } from "configureStore";
import { sendMessage as sendMessageApi } from "services/chat.service";
import {
  upload as uploadFileApi,
  isEmptyUploadError,
} from "services/upload-file.service";
import { ChatMessage } from "types/Chat";
import {
  addOptimisticMessage,
  replaceOptimisticMessage,
  removeOptimisticMessage,
} from "./chat.slice";

interface UploadChatFileOptions {
  dispatch: AppDispatch;
  companyId: string;
  conversationId: string;
  myUserId?: string;
  file: File;
  fileUploadFailedMessage: string;
  onOptimisticMessageAdded?: () => void;
  onError: (message: string) => void;
}

export const uploadChatFileMessage = async ({
  dispatch,
  companyId,
  conversationId,
  myUserId,
  file,
  fileUploadFailedMessage,
  onOptimisticMessageAdded,
  onError,
}: UploadChatFileOptions) => {
  const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const isImage = file.type.startsWith("image/");
  const localPreviewUrl = isImage ? URL.createObjectURL(file) : undefined;

  const optimistic: ChatMessage = {
    id: tempId,
    conversationId,
    sender: myUserId || "",
    content: "",
    attachment: {
      id: "pending",
      name: file.name,
      companyId,
      mimeType: file.type,
      size: file.size,
    },
    createdAt: Date.now(),
    editedAt: null,
    deletedAt: null,
    reactions: [],
    localPreviewUrl,
    uploadStatus: "uploading",
  };

  dispatch(addOptimisticMessage(optimistic));
  onOptimisticMessageAdded?.();

  try {
    const uploadRes = await uploadFileApi(file, companyId);
    const fileId =
      typeof uploadRes?.data === "string"
        ? uploadRes.data
        : uploadRes?.data?.id;
    if (!fileId) throw new Error("Upload returned no fileId");

    const real = await sendMessageApi(companyId, conversationId, { fileId });
    dispatch(replaceOptimisticMessage({ tempId, message: real }));

    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
  } catch (err: any) {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    dispatch(removeOptimisticMessage({ conversationId, tempId }));
    // An empty file was already toasted by upload() — no second message.
    if (isEmptyUploadError(err)) return;
    onError(err?.response?.data?.error || fileUploadFailedMessage);
  }
};
